import React, { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

/**
 * Render a PDF (from Blob/URL) inline. On page click, capture the position in
 * normalized (0..1, bottom-left origin) coords and emit via onChange.
 *
 * Props:
 *  - fileBlob: Blob | ArrayBuffer of the PDF
 *  - positions: array of {page, x_norm, y_norm}
 *  - onChange: (nextPositions) => void
 */
export default function PdfSignPlacer({ fileBlob, positions = [], onChange }) {
  const [pages, setPages] = useState([]); // [{num, viewport, scale}]
  const [pdf, setPdf] = useState(null);
  const canvasRefs = useRef({});

  useEffect(() => {
    if (!fileBlob) return;
    let cancelled = false;
    (async () => {
      const buf = fileBlob instanceof Blob ? await fileBlob.arrayBuffer() : fileBlob;
      const loadingTask = pdfjs.getDocument({ data: buf });
      const p = await loadingTask.promise;
      if (cancelled) return;
      setPdf(p);
      const arr = [];
      for (let i = 1; i <= p.numPages; i++) arr.push({ num: i });
      setPages(arr);
    })();
    return () => { cancelled = true; };
  }, [fileBlob]);

  useEffect(() => {
    if (!pdf) return;
    (async () => {
      for (const p of pages) {
        const el = canvasRefs.current[p.num];
        if (!el || el._rendered) continue;
        const page = await pdf.getPage(p.num);
        const targetWidth = Math.min(720, el.parentElement?.clientWidth || 720);
        const scale = targetWidth / page.getViewport({ scale: 1 }).width;
        const viewport = page.getViewport({ scale });
        el.width = viewport.width;
        el.height = viewport.height;
        el.style.width = `${viewport.width}px`;
        el.style.height = `${viewport.height}px`;
        await page.render({ canvasContext: el.getContext("2d"), viewport }).promise;
        el._rendered = true;
      }
    })();
  }, [pdf, pages]);

  const handleClick = (pageNum, e) => {
    const el = canvasRefs.current[pageNum];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const x_norm = Math.max(0, Math.min(1, cx / rect.width));
    const y_norm = Math.max(0, Math.min(1, 1 - cy / rect.height));
    onChange?.([...(positions || []), { page: pageNum, x_norm, y_norm, size_scale: 1.0 }]);
  };

  const removeAt = (idx) => {
    onChange?.(positions.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-3" data-testid="pdf-sign-placer">
      <div className="text-xs text-slate-600 border-l-2 border-blue-500 bg-blue-50 pl-3 py-2">
        Click on any page to drop a signature stamp. You can drop multiple stamps across pages. Click the ✕ on a marker to remove it.
      </div>
      {pages.map((p) => {
        const stampsOnPage = positions.filter((x) => x.page === p.num);
        return (
          <div key={p.num} className="relative border border-slate-300 bg-slate-50 rounded-sm inline-block max-w-full overflow-hidden" data-testid={`pdf-page-wrap-${p.num}`}>
            <div className="label-micro px-2 py-1 bg-slate-100 border-b border-slate-200">Page {p.num}</div>
            <div className="relative">
              <canvas ref={(el) => { if (el) canvasRefs.current[p.num] = el; }}
                      onClick={(e) => handleClick(p.num, e)}
                      className="cursor-crosshair block max-w-full"
                      data-testid={`pdf-page-canvas-${p.num}`}/>
              {stampsOnPage.map((s, i) => {
                const idx = positions.findIndex(x => x === s);
                const leftPct = s.x_norm * 100;
                const topPct = (1 - s.y_norm) * 100;
                const scale = s.size_scale || 1.0;
                const chgSize = (delta) => {
                  const next = Math.max(0.5, Math.min(2.5, scale + delta));
                  const arr = [...positions];
                  arr[idx] = { ...arr[idx], size_scale: next };
                  onChange?.(arr);
                };
                // Compute visual size: base 200x90pt maps to a rough pixel size for preview
                const w = 100 * scale, h = 45 * scale;
                return (
                  <div key={idx} data-testid={`sig-marker-${idx}`}
                       className="absolute -translate-x-1/2 -translate-y-1/2 border-2 border-emerald-500 bg-emerald-100/70 text-emerald-900 shadow-sm"
                       style={{ left: `${leftPct}%`, top: `${topPct}%`, width: `${w}px`, height: `${h}px` }}>
                    <div className="text-[9px] font-semibold px-1 pt-0.5 flex items-center justify-between">
                      <span>#{idx + 1}</span>
                      <div className="pointer-events-auto flex gap-0.5">
                        <button onClick={(e) => { e.preventDefault(); chgSize(-0.25); }} className="px-1 hover:bg-emerald-200 rounded" aria-label="Shrink">−</button>
                        <button onClick={(e) => { e.preventDefault(); chgSize(0.25); }} className="px-1 hover:bg-emerald-200 rounded" aria-label="Grow">+</button>
                        <button onClick={(e) => { e.preventDefault(); removeAt(idx); }} className="px-1 hover:bg-red-200 rounded text-red-700" aria-label="Remove">✕</button>
                      </div>
                    </div>
                    <div className="text-[9px] text-center italic mt-1">Sign here · {Math.round(scale * 100)}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
